import axios from 'axios';
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Table } from 'react-bootstrap';
import dayjs from 'dayjs';
import { getAdjustedStartDate, getAdjustedEndDate } from '../../utils/dateHelper';

const ExpenseRecognitionReport = () => {
    // Initialize with empty dates or current month range
    const [dateRange, setDateRange] = useState({
        startDate: '',
        endDate: ''
    });
    const [reportData, setReportData] = useState([]);
    const [summary, setSummary] = useState({});
    const [loading, setLoading] = useState(false);

    // Optional: Set default to current month on component mount
    //   useEffect(() => {
    //     const today = new Date();
    //     const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    //     const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    //     setDateRange({
    //       startDate: firstDay.toISOString().split('T')[0],
    //       endDate: lastDay.toISOString().split('T')[0]
    //     });
    //   }, []);

    // Fetch data when dateRange changes (optional - or only on button click)
    useEffect(() => {
        if (dateRange.startDate && dateRange.endDate) {
            fetchReportData();
        }
    }, [dateRange]); // Be careful with this - it will fetch on every date change

    const handleClearFilter = () => {
        setDateRange({
            startDate: '',
            endDate: ''
        });
        setReportData([]);
        setSummary({});
    };
    const fetchReportData = async () => {
        // Validate dates before making API call
        if (!dateRange.startDate || !dateRange.endDate) {
            alert('Please select both start date and end date');
            return;
        }

        if (dateRange.startDate > dateRange.endDate) {
            alert('Start date cannot be after end date');
            return;
        }

        try {
            setLoading(true);
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/expense-recognition/detailed-report`, {
                params: {
                    startDate: dateRange.startDate,
                    endDate: dateRange.endDate
                },
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
            });

            if (response.data.status === 'success') {
                setReportData(response.data.data);
                setSummary(response.data.summary);
            }
        } catch (error) {
            console.error('Error fetching report data:', error);
            alert('Failed to fetch report data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        if (!dateRange.startDate || !dateRange.endDate) {
            alert('Please select both start date and end date before exporting');
            return;
        }

        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/expense-recognition/export-report`, {
                params: {
                    startDate: dateRange.startDate,
                    endDate: dateRange.endDate,
                    format: 'csv'
                },
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `revenue-report-${dateRange.startDate}-to-${dateRange.endDate}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error downloading report:', error);
            alert('Failed to download report. Please try again.');
        }
    };

    const formatAEDStyle = (amount) => {
        return Number(amount || 0).toLocaleString('en-AE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US');
    };

    const handleDateChange = (field, value) => {
        if (field === 'startDate') {
            const { startDate: newStart, suggestedEndDate } = getAdjustedStartDate(value, dateRange.endDate);
            setDateRange(prev => ({
                ...prev,
                startDate: newStart,
                ...(suggestedEndDate && { endDate: suggestedEndDate })
            }));
        } else {
            setDateRange(prev => ({
                ...prev,
                endDate: getAdjustedEndDate(value, dateRange.startDate)
            }));
        }
    };

    const handleApplyFilter = () => {
        fetchReportData();
    };

    // Quick date range presets
    const setQuickDateRange = (range) => {
        const today = dayjs();
        let startDate, endDate;

        switch (range) {
            case 'thisMonth':
                startDate = today.startOf('month');
                endDate = today.endOf('month');
                break;
            case 'lastMonth':
                startDate = today.subtract(1, 'month').startOf('month');
                endDate = today.subtract(1, 'month').endOf('month');
                break;
            case 'thisQuarter':
                startDate = today.startOf('quarter');
                endDate = today.endOf('quarter');
                break;
            case 'thisYear':
                startDate = today.startOf('year');
                endDate = today.endOf('year');
                break;
            default:
                return;
        }

        setDateRange({
            startDate: startDate.format('YYYY-MM-DD'),
            endDate: endDate.format('YYYY-MM-DD')
        });
    };

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid">
                    {/* Page Title and Filters */}
                    <div className="row">
                        <div className="col-12">
                            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                <h4 className="mb-sm-0 font-size-18">Revenue Recognition Detailed Report</h4>
                            </div>
                        </div>
                    </div>

                    {/* Date Filters */}
                    <div className="row mt-3">
                        <div className="col-12">
                            <Card>
                                <Card.Body>
                                    <div className="row g-3 align-items-end">
                                        <div className="col-md-3">
                                            <label className="form-label">From Date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={dateRange.startDate}
                                                onChange={(e) => handleDateChange('startDate', e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label">To Date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={dateRange.endDate}
                                                onChange={(e) => handleDateChange('endDate', e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label">Quick Select</label>
                                            <div className="d-flex gap-2 flex-wrap">
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => setQuickDateRange('thisMonth')}
                                                >
                                                    This Month
                                                </Button>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => setQuickDateRange('lastMonth')}
                                                >
                                                    Last Month
                                                </Button>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => setQuickDateRange('thisQuarter')}
                                                >
                                                    This Quarter
                                                </Button>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => setQuickDateRange('thisYear')}
                                                >
                                                    This Year
                                                </Button>
                                            </div>
                                        </div>
                                        <div className="col-md-2">
                                            <div className="d-flex gap-2">
                                                <Button
                                                    variant="primary"
                                                    onClick={handleApplyFilter}
                                                    disabled={loading}
                                                    className="w-100"
                                                >
                                                    {loading ? 'Loading...' : 'Apply Filter'}
                                                </Button>
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={handleClearFilter}
                                                    disabled={loading}
                                                    className="flex-grow-1"
                                                >
                                                    Clear
                                                </Button>
                                            </div>
                                        </div>
                                        </div>
                                </Card.Body>
                            </Card>
                    </div>
                </div>

                {/* Summary Cards */}
                {/* Summary Cards */}
                <div className="row mt-3">
                    <div className="col-md-3">
                        <Card className="border-primary">
                            <Card.Body className="bg-light-primary text-dark">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <h6 className="text-muted">Total Recognized in Period</h6>
                                        <h4>{formatAEDStyle(summary.totalRecognizedInPeriod)}</h4>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                    <div className="col-md-3">
                        <Card className="border-success">
                            <Card.Body className="bg-light-success text-dark">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <h6 className="text-muted">Total Deferred Revenue</h6>
                                        <h4>{formatAEDStyle(summary.totalRecognizedInPeriod)}</h4>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                    <div className="col-md-3">
                        <Card className="border-info">
                            <Card.Body className="bg-light-info text-dark">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <h6 className="text-muted">Total Projects</h6>
                                        <h4>{summary.totalExpenses || 0}</h4>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                    <div className="col-md-3">
                        <Card className="border-warning">
                            <Card.Body className="bg-light-warning text-dark">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <h6 className="text-muted">Actions</h6>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={handleDownload}
                                            disabled={loading || reportData.length === 0}
                                        >
                                            Export CSV
                                        </Button>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                </div>

                {/* Detailed Report Table */}
                <div className="row mt-3">
                    <div className="col-12">
                        <Card>
                            <Card.Body>
                                {loading ? (
                                    <div className="text-center p-4">Loading report data...</div>
                                ) : (
                                    <div className="table-responsive">
                                        <Table className="table align-middle table-nowrap table-hover">
                                            <thead className="table-sales-gradient">
                                                <tr>
                                                    <th>#</th>
                                                    <th>Expense</th>
                                                    <th>Vendor</th>
                                                    <th>Start Date</th>
                                                    <th>End Date</th>
                                                    <th className="text-end">Total Amount</th>
                                                    <th className="text-end">Recognized Amount</th>
                                                    <th className="text-end">Recognized to Date</th>

                                                    <th className="text-end">Deferred Expense</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {reportData.map((item,index) => (
                                                    <tr key={item.projectId}>
                                                        <td>{index+1}</td>
                                                        <td>{item.expenseName}</td>
                                                        <td>{item.vendorName}</td>
                                                        <td>{formatDate(item.startDate)}</td>
                                                        <td>{formatDate(item.endDate)}</td>
                                                        <td className="text-end">{formatAEDStyle(item.totalAmount)}</td>
                                                        <td className="text-end">{formatAEDStyle(item.recognizedInPeriod)}</td>
                                                        <td className="text-end">{formatAEDStyle(item.totalRecognizedToDate)}</td>
                                                        <td className="text-end">{formatAEDStyle(item.balance)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                        {reportData.length === 0 && !loading && (
                                            <div className="text-center p-4 text-muted">
                                                {dateRange.startDate && dateRange.endDate
                                                    ? 'No revenue recognition data found for the selected period.'
                                                    : 'Please select a date range to view the report.'
                                                }
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    </div >
  );
};

export default ExpenseRecognitionReport;
